/**
 * Route Response Parser
 * Separates API response parsing from UI rendering logic.
 * Maps maneuver types to direction icons and formats step data.
 */

/**
 * Maneuver type to icon mapping based on Valhalla API types
 * Reference: https://valhalla.github.io/valhalla/api/turn-by-turn/api-reference/
 */
const MANEUVER_TYPE_ICONS = {
  0: '/icons/depart_straight.svg',      // kNone
  1: '/icons/depart_straight.svg',      // kStart
  2: '/icons/depart_right.svg',         // kStartRight
  3: '/icons/depart_left.svg',          // kStartLeft
  4: '/icons/arrive_straight.svg',      // kDestination
  5: '/icons/arrive_right.svg',         // kDestinationRight
  6: '/icons/arrive_left.svg',          // kDestinationLeft
  7: '/icons/continue_straight.svg',    // kBecomes
  8: '/icons/continue_straight.svg',    // kContinue
  9: '/icons/slight_right.svg',         // kSlightRight
  10: '/icons/turn_right.svg',          // kRight
  11: '/icons/sharp_right.svg',         // kSharpRight
  12: '/icons/uturn_right.svg',         // kUturnRight
  13: '/icons/uturn_left.svg',          // kUturnLeft
  14: '/icons/sharp_left.svg',          // kSharpLeft
  15: '/icons/turn_left.svg',           // kLeft
  16: '/icons/slight_left.svg',         // kSlightLeft
  17: '/icons/continue_straight.svg',   // kRampStraight
  18: '/icons/slight_right.svg',        // kRampRight
  19: '/icons/slight_left.svg',         // kRampLeft
  20: '/icons/exit_right.svg',          // kExitRight
  21: '/icons/exit_left.svg',           // kExitLeft
  22: '/icons/slight_right.svg',        // kStayStraight
  23: '/icons/slight_right.svg',        // kStayRight
  24: '/icons/slight_left.svg',         // kStayLeft
  25: '/icons/merge_straight.svg',      // kMerge
  26: '/icons/roundabout_clockwise.svg', // kRoundaboutEnter
  27: '/icons/roundabout_clockwise.svg', // kRoundaboutExit
  28: '/icons/fork_slight_right.svg',   // kFerryEnter
  29: '/icons/fork_slight_left.svg',    // kFerryExit
};

/**
 * Get the direction icon path for a given maneuver type
 * @param {number} type - Maneuver type from API
 * @returns {string} Path to the icon file
 */
export const getManeuverIcon = (type) => {
  return MANEUVER_TYPE_ICONS[type] || '/icons/continue_straight.svg';
};

/**
 * Format distance for individual step display
 * @param {number} km - Distance in kilometers
 * @returns {string} Formatted distance string
 */
export const formatStepDistance = (km) => {
  if (!km && km !== 0) return '';
  if (km === 0) return '';
  if (km < 0.1) {
    const meters = Math.round(km * 1000);
    return `${meters} m`;
  }
  if (km < 1) {
    const meters = Math.round(km * 1000 / 10) * 10;
    return `${meters} m`;
  }
  return `${km.toFixed(1)} km`;
};

/**
 * Format duration for display
 * @param {number} seconds - Duration in seconds
 * @returns {string} Formatted duration string
 */
export const formatRouteDuration = (seconds) => {
  if (!seconds) return '0 min';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  if (hours > 0) {
    return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
  }
  return `${minutes} min`;
};

/**
 * Format total distance for route summary
 * @param {number} meters - Distance in meters
 * @returns {string} Formatted distance string
 */
export const formatRouteDistance = (meters) => {
  if (!meters) return '0 km';
  const km = meters / 1000;
  if (km < 1) {
    return `${Math.round(km * 1000)} m`;
  }
  return `${km.toFixed(1)} km`;
};

/**
 * Parse route legs/maneuvers into structured step data for UI rendering.
 * In multi-stop routes, inserts a waypoint marker between legs to show
 * intermediate destinations (matching Figma design).
 * @param {Array} legs - Route legs from API response
 * @param {Array} stops - Optional intermediate stop details [{name, address}, ...]
 * @returns {Array} Parsed steps ready for rendering
 */
export const parseRouteSteps = (legs, stops = []) => {
  if (!legs || !Array.isArray(legs) || legs.length === 0) return [];

  const steps = [];
  const isMultiLeg = legs.length > 1;

  legs.forEach((leg, legIndex) => {
    if (!leg.maneuvers || !Array.isArray(leg.maneuvers)) return;

    leg.maneuvers.forEach((maneuver) => {
      // In multi-leg routes, skip the final "arrive" maneuver of intermediate legs
      // because we'll insert a waypoint marker instead
      const isArriveType = maneuver.type >= 4 && maneuver.type <= 6;
      if (isMultiLeg && isArriveType && legIndex < legs.length - 1) {
        return; // Skip — waypoint marker replaces this
      }

      steps.push({
        instruction: maneuver.instruction || '',
        distance: maneuver.length || 0, // in km
        duration: maneuver.time || 0, // in seconds
        type: maneuver.type,
        icon: getManeuverIcon(maneuver.type),
        streetNames: maneuver.street_names || [],
        verbalInstruction: maneuver.verbal_pre_transition_instruction || '',
        postInstruction: maneuver.verbal_post_transition_instruction || '',
        bearingAfter: maneuver.bearing_after,
        bearingBefore: maneuver.bearing_before,
        isDepart: maneuver.type >= 1 && maneuver.type <= 3,
        isArrive: maneuver.type >= 4 && maneuver.type <= 6,
        isWaypoint: false,
        isFinalDestination: false,
      });
    });

    // After each intermediate leg, insert a waypoint destination marker
    if (isMultiLeg && legIndex < legs.length - 1) {
      const stopInfo = stops[legIndex] || {};
      steps.push({
        instruction: stopInfo.name || stopInfo.shortName || `Stop ${legIndex + 1}`,
        address: stopInfo.address || stopInfo.entity_address || stopInfo.display_address || '',
        distance: 0,
        duration: 0,
        type: -1, // Special type for waypoint
        icon: '/icons/add.svg',
        streetNames: [],
        verbalInstruction: '',
        postInstruction: '',
        bearingAfter: null,
        bearingBefore: null,
        isDepart: false,
        isArrive: false,
        isWaypoint: true,
        isFinalDestination: false,
      });
    }
  });

  // Mark the last step as final destination in multi-leg routes
  if (isMultiLeg && steps.length > 0) {
    const lastStep = steps[steps.length - 1];
    if (lastStep.isArrive) {
      lastStep.isFinalDestination = true;
      lastStep.icon = '/icons/where-to-vote.png';
    }
  }

  return steps;
};

/**
 * Calculate ETA from duration
 * @param {number} seconds - Duration in seconds
 * @returns {string} ETA time string
 */
export const calculateETA = (seconds) => {
  if (!seconds) return '';
  const now = new Date();
  now.setSeconds(now.getSeconds() + seconds);
  return now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Validate route response data
 * @param {Object} routeDetails - Route details object
 * @returns {boolean} Whether the route data is valid for rendering
 */
export const isValidRouteData = (routeDetails) => {
  if (!routeDetails) return false;
  if (!routeDetails.legs || !Array.isArray(routeDetails.legs)) return false;
  if (routeDetails.legs.length === 0) return false;
  return routeDetails.legs.some(leg => 
    leg.maneuvers && Array.isArray(leg.maneuvers) && leg.maneuvers.length > 0
  );
};
